/* ==========================================================================
   BRITO COSMETICS — app.js  ·  Motor de interacción + movimiento
   --------------------------------------------------------------------------
   · Conserva hooks: data-reveal, data-slider, data-quiz-*, data-header,
     data-menu-toggle/data-drawer, data-cart-count.
   · Añade: Lenis (scroll suave) + GSAP/ScrollTrigger (parallax, scroll
     horizontal, líneas), botón magnético, marquee continuo, carrito drawer
     AJAX, búsqueda predictiva y transiciones de página.
   · Degradación elegante: sin GSAP usa IntersectionObserver; respeta
     prefers-reduced-motion; funciona en el preview estático y en Shopify.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITOR   = !!(window.Shopify && window.Shopify.designMode);
  var GSAP     = window.gsap;
  var ST       = window.ScrollTrigger;
  var HAS_GSAP = !!(GSAP && ST) && !REDUCED;
  var THEME    = window.theme || (window.theme = {});
  var ROUTES   = THEME.routes || {};
  var STR      = THEME.strings || {};
  var IS_SHOPIFY = !!ROUTES.cart_add_url;

  if (HAS_GSAP) { GSAP.registerPlugin(ST); ST.config({ ignoreMobileResize: true }); }

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ======================================================================
     1 · SCROLL SUAVE (Lenis) sincronizado con GSAP
     ====================================================================== */
  var lenis = null;
  function initLenis() {
    if (REDUCED || EDITOR || !window.Lenis) return;
    lenis = new window.Lenis({
      duration: 1.1,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.6
    });
    document.documentElement.classList.add('has-lenis');
    if (HAS_GSAP) {
      lenis.on('scroll', ST.update);
      GSAP.ticker.add(function (time) { lenis.raf(time * 1000); });
      GSAP.ticker.lagSmoothing(0);
    } else {
      var raf = function (t) { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    THEME.lenis = lenis;
  }
  function scrollTo(target, opts) {
    if (lenis) lenis.scrollTo(target, opts || {});
    else if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ======================================================================
     2 · REVEALS (clase .is-visible → transiciones CSS)
        ScrollTrigger si hay GSAP; si no, IntersectionObserver.
     ====================================================================== */
  function reveal(el) { el.classList.add('is-visible'); }
  function showAll(root) {
    (root || document).querySelectorAll('[data-reveal], .line-mask, [data-curtain]').forEach(reveal);
  }
  var io = null;
  function observeReveals(root) {
    var sel = '[data-reveal]:not(.is-visible), .line-mask:not(.is-visible), [data-curtain]:not(.is-visible)';
    var els = (root || document).querySelectorAll(sel);
    if (EDITOR || REDUCED) { els.forEach(reveal); return; }
    if (HAS_GSAP) {
      els.forEach(function (el) {
        ST.create({ trigger: el, start: 'top 88%', once: true, onEnter: function () { reveal(el); } });
      });
      return;
    }
    if (!io) {
      if (!('IntersectionObserver' in window)) { els.forEach(reveal); return; }
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    }
    els.forEach(function (el) { io.observe(el); });
  }

  /* ======================================================================
     3 · PARALLAX  [data-parallax="0.2"]  (sólo GSAP)
     ====================================================================== */
  function initParallax(root) {
    if (!HAS_GSAP) return;
    (root || document).querySelectorAll('[data-parallax]').forEach(function (el) {
      var amt = parseFloat(el.getAttribute('data-parallax')) || 0.15;
      GSAP.fromTo(el, { yPercent: -amt * 50 }, {
        yPercent: amt * 50, ease: 'none',
        scrollTrigger: { trigger: el.closest('[data-parallax-wrap]') || el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* ======================================================================
     4 · BOTÓN / IMAGEN MAGNÉTICO  [data-magnetic]
     ====================================================================== */
  function initMagnetic(root) {
    if (REDUCED || window.matchMedia('(hover: none)').matches) return;
    (root || document).querySelectorAll('[data-magnetic]').forEach(function (el) {
      if (el.__mag) return; el.__mag = true;
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.35;
      var inner = el.querySelector('[data-magnetic-inner]') || el;
      var move = function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        if (GSAP) GSAP.to(inner, { x: x, y: y, duration: 0.5, ease: 'power3.out' });
        else inner.style.transform = 'translate(' + x + 'px,' + y + 'px)';
      };
      var reset = function () {
        if (GSAP) GSAP.to(inner, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1,0.4)' });
        else inner.style.transform = '';
      };
      el.addEventListener('mousemove', move);
      el.addEventListener('mouseleave', reset);
    });
  }

  /* ======================================================================
     5 · SCROLL HORIZONTAL  [data-hscroll]  (pin + scrub)
        Fallback sin GSAP: scroll nativo overflow-x (CSS).
     ====================================================================== */
  function initHScroll(root) {
    if (!HAS_GSAP) return;
    (root || document).querySelectorAll('[data-hscroll]').forEach(function (section) {
      var track = section.querySelector('[data-hscroll-track]');
      if (!track) return;
      if (window.matchMedia('(max-width: 760px)').matches) return; // móvil: scroll nativo
      track.style.willChange = 'transform';
      var getDist = function () { return track.scrollWidth - section.offsetWidth; };
      GSAP.to(track, {
        x: function () { return -getDist(); }, ease: 'none',
        scrollTrigger: {
          trigger: section, start: 'top top', pin: true, scrub: 1,
          end: function () { return '+=' + getDist(); }, invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });
    });
  }

  /* ======================================================================
     6 · MARQUEE continuo (duplica contenido para bucle sin saltos)
     ====================================================================== */
  function initMarquee(root) {
    // Duplica los items DENTRO de la misma pista → el translateX(-50%) hace bucle continuo.
    (root || document).querySelectorAll('.marquee__track:not([data-mq-ready])').forEach(function (track) {
      track.setAttribute('data-mq-ready', '');
      track.insertAdjacentHTML('beforeend', track.innerHTML);
    });
  }

  /* ======================================================================
     7 · HEADER sticky + estado sobre hero
     ====================================================================== */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var last = 0;
    var onScroll = function () {
      var y = lenis ? lenis.scroll : window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      // Ocultar al bajar, reaparecer al subir (solo pasado el primer tramo)
      if (y > 280 && !document.body.classList.contains('is-locked')) {
        header.classList.toggle('is-hidden', y > last + 6);
      } else {
        header.classList.remove('is-hidden');
      }
      last = y;
    };
    onScroll();
    if (lenis) lenis.on('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ======================================================================
     8 · DRAWER de menú móvil
     ====================================================================== */
  function lockScroll(lock) {
    document.body.classList.toggle('is-locked', lock);
    if (lenis) { lock ? lenis.stop() : lenis.start(); }
    document.body.style.overflow = lock ? 'hidden' : '';
  }
  function initMenuDrawer() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var drawer = document.querySelector('[data-drawer]');
    if (!toggle || !drawer) return;
    var set = function (open) {
      drawer.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      lockScroll(open);
    };
    toggle.addEventListener('click', function () { set(!drawer.classList.contains('is-open')); });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () { set(false); });
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') set(false); });
  }

  /* ======================================================================
     9 · HERO slider (auto + dots) — con animación de entrada por slide
     ====================================================================== */
  function initSliders(root) {
    (root || document).querySelectorAll('[data-slider]').forEach(function (slider) {
      if (slider.__init) return; slider.__init = true;
      var slides = slider.querySelectorAll('[data-slide]');
      var dots = slider.querySelectorAll('[data-dot]');
      if (slides.length < 2) return;
      var i = 0, timer;
      var go = function (n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
        dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === i); });
      };
      var play = function () { if (!REDUCED) timer = setInterval(function () { go(i + 1); }, 6500); };
      var stop = function () { clearInterval(timer); };
      dots.forEach(function (d, idx) { d.addEventListener('click', function () { stop(); go(idx); play(); }); });
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', play);
      go(0); play();
    });
  }

  /* ======================================================================
     10 · QUIZ capilar (multipaso)
     ====================================================================== */
  function initQuiz(root) {
    var steps = root.querySelectorAll('[data-quiz-step]');
    var bar = root.querySelector('[data-quiz-bar]');
    var answers = {}, current = 0;
    function show(n) {
      current = n;
      steps.forEach(function (s, idx) { s.classList.toggle('is-active', idx === n); });
      if (bar) bar.style.width = (n / (steps.length - 1) * 100) + '%';
      scrollTo(root, { offset: -90 });
    }
    root.querySelectorAll('[data-quiz-option]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        var step = opt.closest('[data-quiz-step]');
        answers[step.getAttribute('data-key')] = opt.getAttribute('data-value');
        step.querySelectorAll('[data-quiz-option]').forEach(function (o) { o.classList.remove('is-selected'); });
        opt.classList.add('is-selected');
        if (current < steps.length - 1) setTimeout(function () { show(current + 1); }, 280);
        else finish();
      });
    });
    root.querySelectorAll('[data-quiz-back]').forEach(function (b) {
      b.addEventListener('click', function () { if (current > 0) show(current - 1); });
    });
    function finish() {
      var result = root.querySelector('[data-quiz-result]');
      var link = root.querySelector('[data-quiz-link]');
      var terms = answers.objetivo || answers.tipo || answers.estado || answers.frecuencia
            || Object.keys(answers).map(function (k) { return answers[k]; }).join(' ');
      if (link) link.href = (root.getAttribute('data-search-url') || '#') + encodeURIComponent(terms);
      var summary = root.querySelector('[data-quiz-summary]');
      if (summary) summary.textContent = (answers.tipo || '') + ' · ' + (answers.objetivo || '');
      steps.forEach(function (s) { s.classList.remove('is-active'); });
      if (bar) bar.style.width = '100%';
      if (result) result.classList.add('is-active');
    }
    show(0);
  }

  /* ======================================================================
     11 · CARRITO DRAWER + AJAX
     ====================================================================== */
  function money(cents) {
    if (typeof cents !== 'number') return '';
    var s = (cents / 100).toFixed(2).replace('.', ',');
    return s + ' €';
  }
  function openCart() {
    var d = document.querySelector('[data-cart-drawer]');
    if (!d) return; d.classList.add('is-open'); lockScroll(true);
    d.setAttribute('aria-hidden', 'false');
  }
  function closeCart() {
    var d = document.querySelector('[data-cart-drawer]');
    if (!d) return; d.classList.remove('is-open'); lockScroll(false);
    d.setAttribute('aria-hidden', 'true');
  }
  function renderCart(cart) {
    var wrap = document.querySelector('[data-cart-items]');
    var sub = document.querySelector('[data-cart-subtotal]');
    var counts = document.querySelectorAll('[data-cart-count]');
    var empty = document.querySelector('[data-cart-empty]');
    var foot = document.querySelector('[data-cart-foot]');
    counts.forEach(function (c) { c.textContent = cart.item_count; c.classList.toggle('is-empty', cart.item_count === 0); });
    if (sub) sub.textContent = money(cart.total_price);
    if (empty) empty.style.display = cart.item_count ? 'none' : '';
    if (foot) foot.style.display = cart.item_count ? '' : 'none';
    if (!wrap) return;
    if (!cart.item_count) { wrap.innerHTML = ''; return; }
    wrap.innerHTML = cart.items.map(function (it) {
      var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';
      return '' +
      '<div class="cd-line" data-line-key="' + it.key + '">' +
        '<a class="cd-line__media" href="' + it.url + '">' + (img ? '<img src="' + img + '" alt="' + (it.product_title || '') + '" loading="lazy">' : '') + '</a>' +
        '<div class="cd-line__info">' +
          '<a class="cd-line__title" href="' + it.url + '">' + it.product_title + '</a>' +
          (it.variant_title && it.variant_title !== 'Default Title' ? '<div class="cd-line__variant">' + it.variant_title + '</div>' : '') +
          '<div class="cd-line__bottom">' +
            '<div class="cd-qty" data-qty>' +
              '<button data-qty-down aria-label="Restar">–</button>' +
              '<span class="mono-num">' + it.quantity + '</span>' +
              '<button data-qty-up aria-label="Sumar">+</button>' +
            '</div>' +
            '<div class="cd-line__price mono-num">' + money(it.final_line_price) + '</div>' +
          '</div>' +
        '</div>' +
        '<button class="cd-line__remove" data-line-remove aria-label="Quitar">✕</button>' +
      '</div>';
    }).join('');
  }
  function fetchCart() {
    return fetch(ROUTES.cart_url ? ROUTES.cart_url + '.js' : '/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); }).then(function (cart) { renderCart(cart); return cart; });
  }
  function changeLine(key, qty) {
    return fetch((ROUTES.cart_change_url || '/cart/change') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); }).then(function (cart) { renderCart(cart); return cart; });
  }
  function addToCart(form, btn) {
    var data = new FormData(form);
    if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    return fetch((ROUTES.cart_add_url || '/cart/add') + '.js', {
      method: 'POST', headers: { 'Accept': 'application/json' }, body: data
    }).then(function (r) { return r.json(); })
      .then(function () { return fetchCart(); })
      .then(function () { openCart(); })
      .catch(function () {})
      .finally(function () { if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
  }
  function initCart() {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        var d = document.querySelector('[data-cart-drawer]');
        if (!d) return; // sin drawer: deja el comportamiento por defecto (ir a /cart)
        e.preventDefault();
        if (d.classList.contains('is-open')) { closeCart(); return; }
        if (IS_SHOPIFY) fetchCart();
        openCart();
      });
    });
    document.querySelectorAll('[data-cart-close]').forEach(function (b) { b.addEventListener('click', closeCart); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeCart(); });

    if (!IS_SHOPIFY) return; // sin backend Shopify (preview), no interceptamos formularios

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"], form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      addToCart(form, form.querySelector('[type="submit"]'));
    });
    document.addEventListener('click', function (e) {
      var line = e.target.closest('[data-line-key]');
      if (!line) return;
      var key = line.getAttribute('data-line-key');
      var qtyEl = line.querySelector('[data-qty] span');
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      if (e.target.closest('[data-qty-up]')) changeLine(key, qty + 1);
      else if (e.target.closest('[data-qty-down]')) changeLine(key, Math.max(0, qty - 1));
      else if (e.target.closest('[data-line-remove]')) changeLine(key, 0);
    });
    fetchCart();
  }

  /* ======================================================================
     12 · BÚSQUEDA PREDICTIVA (overlay)
     ====================================================================== */
  function initSearch() {
    var open = document.querySelectorAll('[data-search-toggle]');
    var overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;
    var input = overlay.querySelector('[data-search-input]');
    var out = overlay.querySelector('[data-search-results]');
    var setOpen = function (o) {
      overlay.classList.toggle('is-open', o); lockScroll(o);
      overlay.setAttribute('aria-hidden', o ? 'false' : 'true');
      if (o && input) setTimeout(function () { input.focus(); }, 60);
    };
    open.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); setOpen(true); }); });
    overlay.querySelectorAll('[data-search-close]').forEach(function (b) { b.addEventListener('click', function () { setOpen(false); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setOpen(false); });
    if (!IS_SHOPIFY || !input || !out) return;
    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      t = setTimeout(function () {
        var url = (ROUTES.predict_url || '/search/suggest.json') + '?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
        fetch(url).then(function (r) { return r.json(); }).then(function (d) {
          var items = (d.resources && d.resources.results && d.resources.results.products) || [];
          if (!items.length) { out.innerHTML = '<p class="muted">Sin resultados para “' + q + '”.</p>'; return; }
          out.innerHTML = items.map(function (p) {
            var img = (p.image || (p.featured_image && p.featured_image.url) || '');
            return '<a class="ps-row" href="' + p.url + '">' +
              (img ? '<img src="' + img + '" alt="" loading="lazy">' : '<span class="ps-row__ph"></span>') +
              '<span class="ps-row__t">' + p.title + '</span>' +
              '<span class="ps-row__p mono-num">' + (p.price ? p.price : '') + '</span></a>';
          }).join('');
        }).catch(function () {});
      }, 220);
    });
  }

  /* ======================================================================
     13 · TRANSICIONES DE PÁGINA (View Transitions + fallback fade)
     ====================================================================== */
  function initPageTransitions() {
    if (REDUCED || EDITOR || !IS_SHOPIFY) return;
    var supportsVT = 'startViewTransition' in document;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || a.target === '_blank' || a.hasAttribute('download') || a.dataset.noTransition !== undefined) return;
      if (a.origin !== location.origin) return;
      if (href.indexOf('#') === 0 || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (href.indexOf('/cart') === 0 || href.indexOf('/checkout') === 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      document.documentElement.classList.add('is-leaving');
      if (!supportsVT) { /* el fade lo gobierna CSS .is-leaving */ }
    });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) document.documentElement.classList.remove('is-leaving');
    });
  }

  /* ======================================================================
     14 · ACORDEONES exclusivos (PDP) — abre uno, cierra el resto (opcional)
     ====================================================================== */
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-accordion]').forEach(function (group) {
      group.querySelectorAll('details').forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (d.open && group.hasAttribute('data-accordion-exclusive')) {
            group.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) o.open = false; });
          }
        });
      });
    });
  }

  /* ======================================================================
     15 · INTRO de carga (cortina + logo) — una vez por sesión
     ====================================================================== */
  function initIntro() {
    var intro = document.querySelector('[data-intro]');
    if (!intro) return;
    var seen; try { seen = sessionStorage.getItem('brito_intro'); } catch (e) {}
    if (REDUCED || EDITOR || seen) { intro.parentNode && intro.parentNode.removeChild(intro); return; }
    try { sessionStorage.setItem('brito_intro', '1'); } catch (e) {}
    document.body.classList.add('intro-active'); lockScroll(true);
    setTimeout(function () { intro.classList.add('is-logo'); }, 80);
    setTimeout(function () { intro.classList.add('is-leaving'); }, 1000);
    setTimeout(function () {
      intro.parentNode && intro.parentNode.removeChild(intro);
      document.body.classList.remove('intro-active'); lockScroll(false);
      if (HAS_GSAP) ST.refresh();
    }, 1750);
  }

  /* ======================================================================
     16 · CONTADORES animados [data-count]
     ====================================================================== */
  function initCounters(root) {
    (root || document).querySelectorAll('[data-count]').forEach(function (el) {
      if (el.__c) return; el.__c = true;
      var target = parseFloat(el.getAttribute('data-count')) || 0;
      var pre = el.getAttribute('data-prefix') || '', suf = el.getAttribute('data-suffix') || '';
      var run = function () {
        if (REDUCED) { el.textContent = pre + target + suf; return; }
        var t0 = null, dur = 1300;
        var step = function (ts) {
          if (!t0) t0 = ts; var p = Math.min(1, (ts - t0) / dur);
          el.textContent = pre + Math.floor((1 - Math.pow(1 - p, 3)) * target) + suf;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      if (HAS_GSAP) ST.create({ trigger: el, start: 'top 92%', once: true, onEnter: run });
      else run();
    });
  }

  /* ======================================================================
     17 · CURSOR personalizado (fino, premium) — sólo puntero fino
     ====================================================================== */
  function initCursor() {
    if (REDUCED || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var dot = document.createElement('div'); dot.className = 'cursor';
    var label = document.createElement('span'); label.className = 'cursor__label';
    dot.appendChild(label); document.body.appendChild(dot);
    document.body.classList.add('has-cursor');
    var x = -100, y = -100, cx = -100, cy = -100;
    document.addEventListener('mousemove', function (e) { x = e.clientX; y = e.clientY; }, { passive: true });
    (function raf() { cx += (x - cx) * 0.22; cy += (y - cy) * 0.22; dot.style.transform = 'translate(' + cx + 'px,' + cy + 'px)'; requestAnimationFrame(raf); })();
    document.addEventListener('mouseover', function (e) {
      var t = e.target.closest('[data-cursor], a, button');
      if (!t) return;
      dot.classList.add('is-active');
      var l = t.getAttribute('data-cursor');
      if (l) { dot.classList.add('is-label'); label.textContent = l; }
    });
    document.addEventListener('mouseout', function (e) {
      if (!e.target.closest('[data-cursor], a, button')) return;
      dot.classList.remove('is-active', 'is-label'); label.textContent = '';
    });
  }

  /* ======================================================================
     INIT
     ====================================================================== */
  function initSection(root) {
    observeReveals(root); initParallax(root); initMagnetic(root);
    initHScroll(root); initMarquee(root); initSliders(root); initAccordions(root);
    initCounters(root);
    var quiz = (root || document).querySelector('[data-quiz]');
    if (quiz && !quiz.__init) { quiz.__init = true; initQuiz(quiz); }
  }

  ready(function () {
    initLenis();
    initIntro();
    initCursor();
    initHeader();
    initMenuDrawer();
    initCart();
    initSearch();
    initPageTransitions();
    initSection(document);
    if (HAS_GSAP) setTimeout(function () { ST.refresh(); }, 300);
  });

  // Editor de Shopify: re-enganchar secciones añadidas/recargadas
  document.addEventListener('shopify:section:load', function (e) { initSection(e.target); if (HAS_GSAP) ST.refresh(); });
  document.addEventListener('shopify:section:select', function (e) { showAll(e.target); });

  // API expuesta por si algún snippet la necesita
  THEME.openCart = openCart; THEME.closeCart = closeCart; THEME.scrollTo = scrollTo;
})();
